
import React, { useState, useEffect } from 'react';

import { fetchData, decryptImageByIndex } from './decryption-helper.js';

const AuthContext = React.createContext();

const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);

      let width = window.innerWidth;
      let newBreakpoint = 
        width >= 1400 ? 1400 :
        width >= 1200 ? 1200 :
        width >= 992  ? 992  :
        width >= 768  ? 768  :
        width >= 576  ? 576  :
        width >= 480  ? 480  :
        width >= 360  ? 360  :
        0;

      setBreakpoint(prev => (prev !== newBreakpoint ? newBreakpoint : prev));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return breakpoint;
}

export const AuthProvider = ({ children }) => {

  const breakpoint = useBreakpoint();

  const empty = {
    photos:null,
    personal:null,
    education:null,
    professional:null,
    skills:null,
    family:null,
    spiritual:null,
    contact:null
  }

  const [key, setKey] = useState(null);
  const [lang,setLang] = useState("en");

  const [data, setData] = useState(empty);
  const [photos, setPhotos] = useState(new Array(8).fill(undefined));
  const [translations, setTranslations] = useState(null);

  const [isLoaded, setLoaded] = useState(false);


  useEffect(() => {
    let urlParams = new URLSearchParams(window.location.search);
    let _key = urlParams.get("key");
    let _lang = urlParams.get("lang") === "hi" ? "hi" : "en";    

    if (_key && _key.length === 16) {
      setKey(_key);
    }

    if(_lang === "hi"){
      document.documentElement.style.setProperty('--font-family',"'IBM Plex Sans Devanagari', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif");
      setLang(_lang);
    }
  }, []);


  const extractData = async(_lang) => {
    try {
      setLoaded(false);

      let _data = await fetchData(key,_lang || lang);
      setData(_data);
      setLoaded(true);
    } catch (e) {      
      setData(empty);
    }
  };

 const loadLang = async (language) => {
    try {              
      let res = await fetch(`./assets/lang/${language}.json`);
      let data = await res.json();            
      setTranslations(data);
    } catch (error) {
      setTranslations(null);
    }
  }; 

  useEffect(() => {
    loadLang(lang);   
    if(key){
      extractData();
    }else{
      setData(empty);
    }

    if(key && !photos[0]){
      handleUpdatePhoto(0);
    }
  }, [lang,key]);


  
  const changeLang = (language) =>{
    language = !!language ? "en" : "hi";
    const url = new URL(window.location);
    url.searchParams.set('lang', language);
    window.history.replaceState({}, '', url);
    setLang(language);
    document.documentElement.style.setProperty(
      '--font-family',
      language === 'hi'
        ? "'IBM Plex Sans Devanagari', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
        : "'Inter', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
    );
  }


  const handleUpdatePhoto = async(index) => {
    try {
      if(!!photos[index]) return;
      let _data = await decryptImageByIndex(key,index);      
      setPhotos((prevPhotos) => {
        const newPhotos = [...prevPhotos];
        newPhotos[index] = _data;
        return newPhotos;
      });
    } catch (error) {      
      console.error("Failed to update photo:");
    }
  };

  return (
    <AuthContext.Provider value={{
      personal: data.personal || {},
      education: data.education || [],
      professional: data.professional || {},
      skills: data.skills || {},
      family: data.family || {},
      spiritual: data.spiritual || {},
      contact: data.contact || {},
      
      photos,
      handleUpdatePhoto,
      translations,

      isLoaded,

      lang,setLang:changeLang,
      breakpoint,

    }}>
      {!!translations && children}
    </AuthContext.Provider>
  );
};

// Hook to use auth
export const useAuth = () => React.useContext(AuthContext);